import {
    Button,
    dispatchCustomEvent,
    DragElement,
    DragElementArea,
    Heading,
    Icon,
    logger,
    Menu,
    MenuHeading,
    MenuItem,
    Popover,
    Portal,
    stopEvent,
    Text,
    useDialog,
    useDrag,
    useDragEvent,
    useLocalStorage,
    useToast,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { TbDots, TbFilters, TbHash, TbLayoutGridAdd, TbMessageCircle } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import {
    generateRandomNextPosition,
    getCachedProjectView,
    getCorrectParentId,
    getCustomGhostElement,
    getNewPositionForTargetIndex,
    sortHierarchy,
} from '../..//helpers'
import { TeamUserPreview } from '../../components'
import { SlidePanel } from '../../components/slide-panel/slide-panel'
import { CACHE } from '../../constants'
import { useProjects, useProjectUsers } from '../../hooks/api.hooks'
import { useAuth } from '../../hooks/auth.hook'
import { useAppStore } from '../../store/app'
import { CollapseIcon } from './collapse-icon'
import { CreateChannel } from './create-channel'
import { CreateProject } from './create-project'
import { Project } from './project'

export const Team = ({ team, onSettings }) => {
    const { id, name, color } = team
    const { visible, show, hide } = useVisibility(false)
    const [list, setList] = useLocalStorage('FRONTEER_TEAM_PROJECTS_' + team.id, true)
    const [collapsed, setCollapsed] = useLocalStorage('FRONTEER_TEAM_PROJECTS_COLLAPSED_' + team.id, [])
    const { workspaceId } = useParams()
    const [loading, setLoading] = useState(false)
    const [over, setOver] = useState(false)
    const [error, setError] = useState<any>('')
    const [panel, setPanel] = useState(0)
    const projectsAPI = useProjects()
    const navDisplay = useAppStore((state) => state.navDisplay)
    const thread = useAppStore((state) => state.thread)
    const project = useAppStore((state) => state.project)
    const { setCustomGhostElement, setCustomGhostElementRotation } = useDrag()
    const { setDialog, closeDialog } = useDialog()
    const { showToast } = useToast()
    const { getMe, logout, getUserId } = useAuth()
    const navigate = useNavigate()
    const [draggedProjectId, setDraggedProjectId] = useState(null)
    const actions = useAppStore((state) => state.actions)
    const sidebar = useAppStore((state) => state.sidebar)
    const allProjects = useAppStore((state) => state.projects)
    const projectUsersAPI = useProjectUsers()
    const projects = useMemo(() => {
        return sortHierarchy(allProjects.filter((p) => p.teamId == id)).filter(
            (project) =>
                !collapsed.includes(project.parentId) &&
                (draggedProjectId == null ? true : project.parentId != draggedProjectId)
        )
    }, [allProjects, collapsed, draggedProjectId])

    const handleDrop = async (e) => {
        const { detail } = e
        const { origin, target } = detail
        const draggedProject = window[CACHE.NAV_PROJECT_DRAG_CACHE]

        if (
            (target.group == 'projects' || target.group == 'projects-empty') &&
            origin.group == 'projects' &&
            draggedProject
        ) {
            const projectId = draggedProject.id
            const addProject = origin.areaId != id && target.areaId == id
            const moveProject = origin.areaId == id && target.areaId == id

            if (addProject) {
                const parentId = getCorrectParentId(projects, target)
                const position = getNewPositionForTargetIndex(projects, target.index)

                actions.projectUpdate({
                    id: projectId,
                    position,
                    teamId: id,
                    teamColor: color,
                    teamName: name,
                    parentId,
                })

                showToast({
                    anchor: 'bottom-left',
                    message: draggedProject.name + ' has moved teams.',
                })

                window[CACHE.NAV_PROJECT_DRAG_CACHE] = null
                setDraggedProjectId(null)

                await projectsAPI.update(workspaceId, projectId, { position, parent: { id: parentId }, team: { id } })
                await projectUsersAPI.removeAll(workspaceId, projectId)
            }

            if (moveProject) {
                const parentId = getCorrectParentId(projects, target)
                const position = getNewPositionForTargetIndex(projects, target.index)

                actions.projectUpdate({ id: projectId, position, parentId, team: { id, name, color } })

                window[CACHE.NAV_PROJECT_DRAG_CACHE] = null
                setDraggedProjectId(null)

                await projectsAPI.update(workspaceId, projectId, { position, parent: { id: parentId }, team: { id } })
            }
        }
    }

    const handleDragStart = (project) => {
        window[CACHE.NAV_PROJECT_DRAG_CACHE] = project
        setDraggedProjectId(project.id)
    }

    const handleMouseDown = (e) => {
        setCustomGhostElementRotation('0deg')
        setCustomGhostElement(getCustomGhostElement(name, 0))
    }

    const handleProjectAdd = async ({ name, color, isChannel = false }) => {
        setLoading(true)
        setError(null)

        try {
            const project = await projectsAPI.create(workspaceId, {
                name,
                color,
                isClosed: true,
                isPrivate: false,
                isChannel,
                position: generateRandomNextPosition(projects),
                users: [{ id: getUserId() }],
                team: { id: team.id },
            })

            actions.projectAdd({
                ...project,
                teamId: team.id,
                teamColor: team.color,
                teamName: team.name,
                isChannel,
            })
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError('Could not fetch teams')
        }
    }

    const handleNavigate = (project) => {
        actions.setMainLoading(true)
        actions.setMessages([])
        //actions.setSections([])
        //actions.setSections(getFastCache(getFastCacheKey(workspaceId, project.id)), false)

        if (project.isChannel || !!thread.id || (!project.isChannel && sidebar == 'tasks')) {
            if (!!sidebar) actions.setSidebar('overview')
            actions.setThread({ id: null, messages: [] })
            dispatchCustomEvent('start-early-load-for-channel')
        }

        navigate(`/app/workspace/${workspaceId}/c/${project.id}/${getCachedProjectView(project.id)}`)
    }

    const handleCollapsed = (project) => {
        if (collapsed.includes(project.id)) {
            setCollapsed(collapsed.filter((c) => c != project.id))
        } else {
            setCollapsed([...collapsed, project.id])
        }
    }

    useDragEvent('ondrop', handleDrop)

    useEffect(() => {
        if (!visible) setPanel(0)
    }, [visible])

    return (
        <View
            width="100%"
            p="0rem 0.5rem"
            m="0 0 0.8rem 0"
            //style={{ borderTop: '1px solid var(--f-color-border)' }}
            onMouseDown={handleMouseDown}>
            <View
                row
                gap={10}
                width="100%"
                position="relative"
                p="0 1rem 0.25rem 1.25rem"
                m={list ? '0' : '0 0 -0.5rem 0'}
                className="reveal-on-hover"
                // border="1px solid var(--f-color-border-strong)"
                // radius="var(--f-radius)"
                color={color ? color : 'var(--f-color-text-weaker)'}>
                {/*    <Icon
                    icon={TbFiltersFilled}
                    style={{ marginRight: 6, position: 'relative', left: 0, stroke: 'none' }}
                /> */}

                <Heading
                    as="h6"
                    className="nav-section-heading f-ellipsis">
                    {name}
                </Heading>

                <CollapseIcon
                    isOpen={list}
                    onClick={() => setList(!list)}
                />

                <Popover
                    arrow
                    focusTrap
                    width={250}
                    portal={Portal}
                    isVisible={visible}
                    onDismiss={hide}
                    content={
                        <div
                            onMouseDown={stopEvent}
                            onClick={stopEvent}>
                            <SlidePanel panel={panel}>
                                <Menu
                                    width="100%"
                                    border="none">
                                    <MenuHeading>{name}</MenuHeading>
                                    <MenuItem
                                        prefix={<Icon icon={TbLayoutGridAdd} />}
                                        onClick={() => setPanel(1)}>
                                        Create Project
                                    </MenuItem>
                                    <MenuItem
                                        prefix={<Icon icon={TbMessageCircle} />}
                                        onClick={() => setPanel(2)}>
                                        Create Channel
                                    </MenuItem>
                                    <MenuItem
                                        prefix={<Icon icon={TbFilters} />}
                                        onClick={() => {
                                            onSettings()
                                            hide()
                                        }}>
                                        Team Settings
                                    </MenuItem>
                                </Menu>

                                <CreateProject
                                    onBack={() => setPanel(0)}
                                    onCreate={({ name, color }) => {
                                        handleProjectAdd({ name, color })
                                        setPanel(0)
                                        setTimeout(hide, 100)
                                    }}
                                />

                                <CreateChannel
                                    onBack={() => setPanel(0)}
                                    onCreate={({ name, color }) => {
                                        handleProjectAdd({ name, color, isChannel: true })
                                        setPanel(0)
                                        setTimeout(hide, 100)
                                    }}
                                />
                            </SlidePanel>
                        </div>
                    }>
                    <Button
                        subtle
                        width={25}
                        height={25}
                        className={visible ? undefined : 'reveal-on-hover-target'}
                        size="xs"
                        m="0 auto 0 -10px"
                        colorToken="text-weaker"
                        style={{ outlineWidth: 0 }}
                        onMouseDown={stopEvent}
                        onClick={show}>
                        <Icon icon={TbDots} />
                    </Button>
                </Popover>

                {!!workspaceId && (
                    <div
                        onClick={onSettings}
                        className="f-buttonize-outline f-row"
                        style={{ borderRadius: 100, height: 'fit-content', outlineWidth: 2, outlineOffset: -1 }}>
                        <TeamUserPreview
                            workspaceId={workspaceId}
                            teamId={id}
                            size="sm"
                        />
                    </div>
                )}
            </View>

            <div className="nav__team-list">
                {!projects.length && list && (
                    <DragElementArea
                        areaId={id}
                        group="projects-empty"
                        variant="focus"
                        direction="vertical"
                        width="100%">
                        <DragElement width="100%">
                            <Text
                                size="sm"
                                p="0.5rem 1.2rem"
                                colorToken="text-weakest"
                                fontWeight={600}>
                                Nothing here yet.
                            </Text>
                        </DragElement>
                    </DragElementArea>
                )}

                {!!projects.length && list && (
                    <DragElementArea
                        indent
                        areaId={id}
                        group="projects"
                        variant="animated"
                        direction="vertical"
                        targetVariant={{ 'projects-empty': 'focus' }}>
                        {projects.map((project: any, index: number) => (
                            <DragElement
                                key={index}
                                width="100%"
                                indent={project.indent}
                                data-id={project.id}
                                id={project.id}
                                onMouseDown={() => handleDragStart(project)}>
                                <Project
                                    key={project.id}
                                    id={project.id}
                                    name={project.name}
                                    icon={project.icon}
                                    color={project.color}
                                    joinable={project.joinable}
                                    shortcode={project.shortcode}
                                    totalTaskCount={project.totalTaskCount}
                                    completeTaskCount={project.completeTaskCount}
                                    isPrivate={false}
                                    isChannel={project.isChannel}
                                    isChild={project.parentId}
                                    isParent={project.isParent}
                                    isClosed={false}
                                    isMain={false}
                                    excerpt=""
                                    readonly={false}
                                    muted={false}
                                    archived={false}
                                    onNavigate={() => handleNavigate(project)}
                                    onCollapse={() => handleCollapsed(project)}
                                    collapsed={collapsed.includes(project.id)}
                                />
                            </DragElement>
                        ))}
                    </DragElementArea>
                )}
            </div>
        </View>
    )
}
